import { useState, useEffect, useCallback } from 'react';
import { Check, X, Trophy, AlertCircle, Loader2, ArrowRight, RotateCcw } from 'lucide-react';
import { startAdaptiveTest, submitAdaptiveAnswer } from '../../services/adaptiveTest';
import LatexRenderer from '../chat/LatexRenderer';

export default function AdaptiveQuizPlayer({ test, onComplete }) {
    const [phase, setPhase] = useState('intro'); // intro | playing | result
    const [session, setSession] = useState(null);
    const [question, setQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleStart = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await startAdaptiveTest(test.id);
            setSession(res.data.session);
            setQuestion(res.data.question);
            setPhase('playing');
        } catch (e) {
            setError(e.response?.data?.message || 'Testni boshlashda xatolik');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = useCallback(async () => {
        if (!session || selectedAnswer === null || loading) return;

        setLoading(true);
        setError(null);

        try {
            const res = await submitAdaptiveAnswer(session.id, selectedAnswer);
            const data = res.data;

            setFeedback(data.previous);
            setSelectedAnswer(null);

            if (data.status === 'completed') {
                setResult(data.result);
                setPhase('result');
                if (data.result?.passed) onComplete?.();
            } else {
                setTimeout(() => {
                    setFeedback(null);
                    setQuestion(data.next_question);
                    setSession(data.session);
                    setLoading(false);
                }, 1500);
                return;
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Javob yuborishda xatolik');
        }

        setLoading(false);
    }, [session, selectedAnswer, loading, onComplete]);

    if (phase === 'intro') {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{test.title}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Intellektual-adaptiv test</p>
                    </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-5 text-sm text-blue-800 dark:text-blue-200">
                    <p className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        Bu test sizning javoblaringizga qarab qiyinlik darajasini avtomatik o'zgartiradi. Xato qilsangiz osonroq, to'g'ri topsangiz murakkabroq savol beriladi.
                    </p>
                </div>

                {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

                <button
                    onClick={handleStart}
                    disabled={loading}
                    className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium text-sm hover:bg-purple-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Testni boshlash</>}
                </button>
            </div>
        );
    }

    if (phase === 'result' && result) {
        const passed = result.passed;
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-6">
                <div className={`text-center py-6 ${passed ? 'text-green-600' : 'text-red-500'}`}>
                    {passed ? <Trophy className="w-16 h-16 mx-auto mb-3" /> : <X className="w-16 h-16 mx-auto mb-3" />}
                    <h3 className="text-2xl font-bold">{result.score}%</h3>
                    <p className="text-sm font-medium mt-1">
                        {passed ? 'Tabriklaymiz! Test muvaffaqiyatli topshirildi!' : `Test topshirilmadi. O'tish balli: ${test.passing_score}%`}
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                        <div className="font-bold text-gray-900 dark:text-gray-100">{result.total_questions}</div>
                        <div className="text-gray-500 dark:text-gray-400">Savollar</div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3">
                        <div className="font-bold text-green-700 dark:text-green-300">{result.correct}</div>
                        <div className="text-green-600 dark:text-green-400">To'g'ri</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3">
                        <div className="font-bold text-red-700 dark:text-red-300">{result.incorrect}</div>
                        <div className="text-red-600 dark:text-red-400">Xato</div>
                    </div>
                </div>

                <button
                    onClick={() => { setPhase('intro'); setResult(null); setSession(null); setQuestion(null); }}
                    className="w-full mt-5 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition flex items-center justify-center gap-2"
                >
                    <RotateCcw className="w-4 h-4" /> Qayta urinish
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                        ${question?.difficulty_tag === 'easy' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                          question?.difficulty_tag === 'hard' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>
                        {question?.difficulty_tag === 'easy' ? 'Oson' : question?.difficulty_tag === 'hard' ? 'Murakkab' : "O'rta"}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Adaptiv test</span>
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500">
                    {session?.stats?.correct ?? 0} / {((session?.stats?.correct ?? 0) + (session?.stats?.incorrect ?? 0))}
                </div>
            </div>

            {feedback && (
                <div className={`mx-6 mt-4 px-4 py-3 rounded-xl text-sm flex items-start gap-2 ${
                    feedback.is_correct
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                        : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                }`}>
                    {feedback.is_correct ? <Check className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
                    <div>
                        <p>{feedback.is_correct ? "To'g'ri!" : "Javob noto'g'ri."}</p>
                        {!feedback.is_correct && <p className="text-xs mt-0.5 opacity-90">{feedback.recommendation}</p>}
                    </div>
                </div>
            )}

            {question && (
                <div className="p-6">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        <LatexRenderer text={question.text} />
                    </h4>

                    {question.type === 'multiple_choice' && question.answers && (
                        <div className="space-y-2">
                            {question.answers.map((ans) => {
                                const selected = selectedAnswer === ans.id;
                                return (
                                    <button
                                        key={ans.id}
                                        onClick={() => setSelectedAnswer(ans.id)}
                                        disabled={loading}
                                        className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm transition flex items-center gap-3
                                            ${selected ? 'border-purple-500 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'}`}
                                    >
                                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                            ${selected ? 'border-purple-500 bg-purple-500' : 'border-gray-300 dark:border-gray-500'}`}>
                                            {selected && <Check className="w-3 h-3 text-white" />}
                                        </span>
                                        {ans.text}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                {error && <p className="text-sm text-red-500 mb-2">{error}</p>}
                <button
                    onClick={handleSubmit}
                    disabled={selectedAnswer === null || loading}
                    className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium text-sm hover:bg-purple-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Javobni yuborish</>}
                </button>
            </div>
        </div>
    );
}
